import React, { useState } from 'react';
import { v4 as uuid } from 'uuid';
import { IProductList, ORDER_STATUS_ID } from 'types/orders';
import CheckOrAddCommentsButton from 'views/orders/Components/CheckOrAddCommentsButton/CheckOrAddCommentsButton';
import { ICountryDetail } from 'types/countries';
import { formatShortDate } from 'utils/formatDatesAndHours';
import { updateOrderStatus } from 'services/orders';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import { useHistory } from 'react-router-dom';
import './HeaderCard.scss';

const HeaderCard = ({ data, openModal, isCancelled, refreshData, queryParams, setQueryParams }: IHeaderCard) => {
  const { countriesShipments, createdAt, cancelledDate, organizationName } = data;
  const [loadingActivate, setLoadingActivate] = useState<boolean>(false);
  const history = useHistory();

  const formattedCreationDateShort = createdAt ? formatShortDate(createdAt)?.onlyDate : 'Sin fecha';
  const formattedCancelingDate = cancelledDate ? formatShortDate(cancelledDate)?.onlyDate : 'Sin fecha';
  const showActiveOrder = isCancelled && !!cancelledDate;

  const handleFilterByCountry = (e: React.MouseEvent, country: ICountryDetail) => {
    e.stopPropagation();
    const countryName = country?.name;
    if (!countryName) return;
    const currentParams = queryParams || '';
    const queryString = currentParams?.startsWith('?') ? currentParams?.slice(1) : currentParams;
    const urlParams = new URLSearchParams(queryString);
    urlParams.set('country', encodeURIComponent(countryName));
    const newQueryParams = `?${urlParams.toString()}`;
    setQueryParams && setQueryParams(newQueryParams);
  };

  const handleFilterByOrganization = (e: React.MouseEvent, organizationName?: string) => {
    e.stopPropagation();
    if (!organizationName) return;
    const currentParams = queryParams || '';
    const queryString = currentParams?.startsWith('?') ? currentParams?.slice(1) : currentParams;
    const urlParams = new URLSearchParams(queryString);
    urlParams.set('organization', organizationName);
    const newQueryParams = `?${urlParams.toString()}`;
    setQueryParams && setQueryParams(newQueryParams);
  };

  const getShipmentFlags = () => {
    const shipmentsFlags = Array.isArray(countriesShipments)
      ? countriesShipments.map((shipmentCountry: ICountryDetail, index: number) => {
          return (
            <div
              key={`shipmentFlag-${index}-shipmentCountry-${shipmentCountry?.id}`}
              className="shipmentDestinationFlags"
              onClick={e => e.stopPropagation()}
            >
              <div
                onClick={e => handleFilterByCountry(e, shipmentCountry)}
                className="flag"
                style={{ backgroundImage: `url("${shipmentCountry?.flag || ''}")` }}
              />
              <span className="organizationName" onClick={e => handleFilterByOrganization(e, organizationName || '')}>
                {organizationName}
              </span>
            </div>
          );
        })
      : [];
    const firstFlags = shipmentsFlags.slice(0, 2);
    const excessFlags = shipmentsFlags?.length > 2 ? shipmentsFlags?.length - 2 : 0;
    const moreFlagsIndicator = (
      <div key={uuid()} className="flag moreFlags">
        +{excessFlags}
      </div>
    );
    if (Array.isArray(countriesShipments) && countriesShipments?.length <= 2) return shipmentsFlags;
    else return [firstFlags, moreFlagsIndicator];
  };

  const handleActivateOrder = async () => {
    if (!data?.orderId || loadingActivate) return;

    setLoadingActivate(true);
    try {
      const { invoiceNumber, paymentLink, committedPaymentDate, invoice, proofOfPayment } = data;
      const formData = new FormData();
      const bodyForm = {
        invoiceNumber: invoiceNumber,
        paymentLink: paymentLink,
        committedPaymentDate: committedPaymentDate
      };
      const bodyJson = JSON.stringify(bodyForm);
      data && formData.append('body', bodyJson);
      invoice && formData.append('invoice', invoice);
      proofOfPayment && formData.append('proofOfPayment', proofOfPayment);

      await updateOrderStatus(data.orderId, ORDER_STATUS_ID.PENDING, formData);

      displaySuccessNotification({
        customJSXMessage: <>Orden reactivada exitosamente</>
      });

      if (refreshData) {
        await refreshData();
      }
    } catch (error) {
      displayErrorNotification({
        customJSXMessage: <>Error al reactivar la orden. Por favor intenta de nuevo</>
      });
    } finally {
      setLoadingActivate(false);
    }
  };

  const handleNavigateToOrderDetails = () => {
    history.push(`/order/details/${data.orderId}`);
  };

  const handleActivateOrderClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleActivateOrder();
  };

  return (
    <div className={`headerCard ${isCancelled ? 'cancelled' : ''}`} onClick={handleNavigateToOrderDetails}>
      <div className="firstRow">
        <div className="numberOrder">N°{data.orderId}</div>
        <div className="orderStatusDate">
          {showActiveOrder && (
            <div className={`activeOrder ${loadingActivate ? 'loading' : ''}`} onClick={handleActivateOrderClick}>
              {loadingActivate ? 'Activando...' : 'Activar'}
            </div>
          )}
          <span className={`orderStatus ${isCancelled ? 'cancelled' : ''}`}>
            {isCancelled ? 'Cancelada:' : 'Solicitada:'}
          </span>
          <span className={`orderDate ${isCancelled ? 'cancelled' : ''}`}>
            {isCancelled ? formattedCancelingDate : formattedCreationDateShort}
          </span>
        </div>
      </div>
      <div className="secondRow">
        <div className="infoCountry">{countriesShipments && getShipmentFlags()}</div>
        <div className="actionModalComments" onClick={e => e.stopPropagation()}>
          <CheckOrAddCommentsButton openModal={openModal} />
        </div>
      </div>
    </div>
  );
};

interface IHeaderCard {
  data: IProductList;
  openModal: () => void;
  isCancelled?: boolean;
  refreshData?: () => Promise<void>;
  queryParams?: string;
  setQueryParams?: (queryParams: string) => void;
}
export default HeaderCard;
